import type { TransferArea, TransferOverviewQuery } from '../../types/statistikPortalTransfer';
import { currentReportYear } from '../../utils/reportYearRange';

/**
 * URL-Suchparameter <-> `TransferOverviewQuery` fuer die Gesamtuebersicht "Uebertragungen"
 * (D-03, D-04, Sketch 025 Variante C). Zwei reine Funktionen ohne React-/Router-Bezug, Muster
 * `reportsFilterUrl.ts` -- eine Stelle fuer Parsen und Serialisieren, damit Raster (41-08), Liste
 * (41-09) und Link-Zeile (41-07) denselben Filterzustand lesen und schreiben.
 *
 * `year` ist -- anders als in `reportsFilterUrl.ts` -- niemals abwesend: ein fehlender oder
 * ungueltiger Wert faellt auf `currentReportYear()` zurueck (Claude's Discretion, 41-CONTEXT.md --
 * kein Beleg-Suchzustand ohne Jahr, anders als bei den Meldungsfiltern D-05/D-06). `area` ist nur
 * einer der drei bekannten Bereiche oder `null`; `cellType`/`cellPeriod` gehoeren zusammen --
 * fehlt einer der beiden oder ist die Periode keine ganze Zahl, werden beide verworfen.
 */

const AREA_VALUES: readonly TransferArea[] = ['market', 'accident', 'environmental'];

/** Spiegel von `StatistikPortalFieldMapping::UNFALLDATEN_TYPE` (backend, siehe 41-03/41-05). */
export const UNFALLDATEN_TYPE = 'Unfallstatistik';

function parseYear(raw: string | null): number {
  if (raw !== null && /^\d+$/.test(raw)) {
    return Number(raw);
  }
  return currentReportYear();
}

function parseArea(raw: string | null): TransferArea | null {
  return raw !== null && (AREA_VALUES as readonly string[]).includes(raw)
    ? (raw as TransferArea)
    : null;
}

function parseNonEmpty(raw: string | null): string | null {
  return raw && raw.length > 0 ? raw : null;
}

function parsePositiveInt(raw: string | null): number | null {
  if (raw !== null && /^\d+$/.test(raw)) {
    return Number(raw);
  }
  return null;
}

export function parseTransferOverviewSearch(
  search: URLSearchParams | string
): TransferOverviewQuery {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;

  const year = parseYear(params.get('year'));
  const area = parseArea(params.get('area'));
  const type = parseNonEmpty(params.get('type'));

  const rawCellType = parseNonEmpty(params.get('cellType'));
  const cellPeriod = parsePositiveInt(params.get('cellPeriod'));
  const cellPaired = rawCellType !== null && cellPeriod !== null;

  return {
    year,
    area,
    type,
    cellType: cellPaired ? rawCellType : null,
    cellPeriod: cellPaired ? cellPeriod : null,
  };
}

/**
 * Kehrt `parseTransferOverviewSearch` um. Feste Reihenfolge year, area, type, cellType,
 * cellPeriod -- `year` immer gesetzt, die uebrigen nur bei einem Wert.
 */
export function buildTransferOverviewSearch(query: TransferOverviewQuery): string {
  const params = new URLSearchParams();
  params.set('year', String(query.year));
  if (query.area) {
    params.set('area', query.area);
  }
  if (query.type) {
    params.set('type', query.type);
  }
  if (query.cellType !== null && query.cellPeriod !== null) {
    params.set('cellType', query.cellType);
    params.set('cellPeriod', String(query.cellPeriod));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Zielseite fuer "Zur Auswertung" (D-22): Unfallstatistik hat keine eigene Periode in der URL,
 * Markttypen schon (Routen aus `<interfaces>` in 41-06-PLAN.md).
 */
export function evaluationPathFor(
  evaluationType: string,
  year: number,
  period: number
): string {
  if (evaluationType === UNFALLDATEN_TYPE) {
    return `/auswertung/unfalldaten?year=${year}`;
  }
  return `/evaluations/${evaluationType}?year=${year}&period=${period}`;
}
